import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdBlock from '../components/AdBlock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import {
  Search,
  GraduationCap,
  Clock,
  MapPin,
  Filter,
  Star,
  Users,
  BookOpen,
  Monitor,
  Building2,
  Sparkles,
  ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { formatMoney } from '../lib/currency';
import PaginationBar from '../components/PaginationBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 12;

export default function CoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const [hasMore, setHasMore] = useState(false);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({});

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [subcategory, setSubcategory] = useState(searchParams.get('subcategory') || '');
  const [level, setLevel] = useState(searchParams.get('level') || '');
  const [delivery, setDelivery] = useState(searchParams.get('delivery') || '');
  const [isFree, setIsFree] = useState(searchParams.get('free') === 'true');
  const [providerType, setProviderType] = useState(searchParams.get('provider_type') || '');
  const [duration, setDuration] = useState(searchParams.get('duration') || '');
  const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');
  const [country, setCountry] = useState(searchParams.get('country') || '');

  useEffect(() => {
    fetchCourses();
    fetchCategories();
    fetchSubcategories();
    fetchCountries();
  }, [searchParams]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('search')) params.set('search', searchParams.get('search'));
      if (searchParams.get('category')) params.set('category', searchParams.get('category'));
      if (searchParams.get('subcategory')) params.set('subcategory', searchParams.get('subcategory'));
      if (searchParams.get('level')) params.set('level', searchParams.get('level'));
      if (searchParams.get('delivery')) params.set('delivery_mode', searchParams.get('delivery'));
      if (searchParams.get('free') === 'true') params.set('is_free', 'true');
      if (searchParams.get('provider_type')) params.set('provider_type', searchParams.get('provider_type'));
      if (searchParams.get('duration')) params.set('duration', searchParams.get('duration'));
      if (searchParams.get('min_price')) params.set('min_price', searchParams.get('min_price'));
      if (searchParams.get('max_price')) params.set('max_price', searchParams.get('max_price'));
      if (searchParams.get('country')) params.set('country', searchParams.get('country'));
      // The API returns no total, so ask for one extra row to learn whether a next page exists.
      params.set('skip', String((page - 1) * PAGE_SIZE));
      params.set('limit', String(PAGE_SIZE + 1));

      const response = await axios.get(`${API_URL}/api/courses?${params.toString()}`);
      setHasMore(response.data.length > PAGE_SIZE);
      setCourses(response.data.slice(0, PAGE_SIZE));
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      setCourses([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses/categories`);
      const cats = response.data.categories || [];
      setCategories(cats.map(c => typeof c === 'string' ? { id: null, name: c, slug: c } : { id: c.id, name: c.name || c.slug, slug: c.slug || c.name }));
    } catch (error) {
      setCategories([
        "Technology & IT", "Business & Management", "Healthcare & Medicine",
        "Finance & Accounting", "Marketing & Digital", "Human Resources",
        "Project Management", "Data Science & AI", "Design & Creative",
        "Teaching & Education", "Language & Communication", "Personal Development"
      ].map(name => ({ id: null, name, slug: name })));
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses/subcategories`);
      const grouped = {};
      (response.data.subcategories || []).forEach(sub => {
        if (!grouped[sub.category_id]) grouped[sub.category_id] = [];
        grouped[sub.category_id].push(sub);
      });
      setSubcategoriesByCategory(grouped);
    } catch (error) {
      setSubcategoriesByCategory({});
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/countries`);
      setCountries(response.data.countries || []);
    } catch (error) {
      setCountries([]);
    }
  };

  const goToPage = (p) => {
    const params = new URLSearchParams(searchParams);
    if (p > 1) params.set('page', String(p)); else params.delete('page');
    setSearchParams(params);
  };

  // A stale ?page=N beyond the last page falls back to the first page.
  useEffect(() => {
    if (!loading && page > 1 && courses.length === 0) goToPage(1);
  }, [loading, courses.length, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    if (subcategory) params.set('subcategory', subcategory);
    if (level) params.set('level', level);
    if (delivery) params.set('delivery', delivery);
    if (isFree) params.set('free', 'true');
    if (providerType) params.set('provider_type', providerType);
    if (duration) params.set('duration', duration);
    if (minPrice) params.set('min_price', minPrice);
    if (maxPrice) params.set('max_price', maxPrice);
    if (country) params.set('country', country);
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="courses-page">
      <AdBlock position="courses-top" showEmpty />
      {/* Hero */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              <GraduationCap className="w-3 h-3 mr-1" />
              5,000+ Courses Available
            </Badge>
            <h1 className="text-4xl font-bold mb-4">Find the perfect course</h1>
            <p className="text-white/80 text-lg">
              Invest in your future with courses from top universities, colleges, and training providers.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <Card className="shadow-xl">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Search courses..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-12 h-12"
                  data-testid="course-search-input"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full md:w-56 h-12 justify-between font-normal" data-testid="course-category-dropdown-trigger">
                    <span className="truncate">
                      {categories.find(c => c.slug === category)?.name || 'Category'}
                      {subcategory && ` — ${subcategory.replace(/-/g, ' ')}`}
                    </span>
                    <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="start">
                  <DropdownMenuItem onClick={() => { setCategory(''); setSubcategory(''); }}>All Categories</DropdownMenuItem>
                  {categories.map((cat) => {
                    const subs = cat.id ? (subcategoriesByCategory[cat.id] || []) : [];
                    if (subs.length === 0) {
                      return (
                        <DropdownMenuItem key={cat.slug} onClick={() => { setCategory(cat.slug); setSubcategory(''); }}>
                          {cat.name}
                        </DropdownMenuItem>
                      );
                    }
                    return (
                      <DropdownMenuSub key={cat.slug}>
                        <DropdownMenuSubTrigger>{cat.name}</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem onClick={() => { setCategory(cat.slug); setSubcategory(''); }}>All {cat.name}</DropdownMenuItem>
                          {subs.map(sub => (
                            <DropdownMenuItem key={sub.id} onClick={() => { setCategory(cat.slug); setSubcategory(sub.slug || sub.name); }}>
                              {sub.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
              <Select value={level || "all"} onValueChange={(val) => setLevel(val === "all" ? "" : val)}>
                <SelectTrigger className="w-full md:w-40 h-12">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                  <SelectItem value="apprenticeship">Apprenticeship</SelectItem>
                  <SelectItem value="award">Award</SelectItem>
                  <SelectItem value="certificate">Certificate</SelectItem>
                  <SelectItem value="diploma">Diploma</SelectItem>
                  <SelectItem value="higher_diploma">Higher Diploma</SelectItem>
                  <SelectItem value="graduate">Graduate</SelectItem>
                  <SelectItem value="postgraduate">Postgraduate</SelectItem>
                </SelectContent>
              </Select>
              <Select value={providerType || "all"} onValueChange={(val) => setProviderType(val === "all" ? "" : val)}>
                <SelectTrigger className="w-full md:w-52 h-12">
                  <SelectValue placeholder="Offered by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Universities & Course Providers</SelectItem>
                  <SelectItem value="university">Universities only</SelectItem>
                  <SelectItem value="course_provider">Course Providers/Trainers only</SelectItem>
                </SelectContent>
              </Select>
              {countries.length > 0 && (
                <Select value={country || "all"} onValueChange={(val) => setCountry(val === "all" ? "" : val)}>
                  <SelectTrigger className="w-full md:w-40 h-12">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countries.map((c) => (
                      <SelectItem key={c.id} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button type="submit" className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700">
                Search
              </Button>
            </form>

            <form onSubmit={handleSearch} className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
              <Select value={delivery || "all"} onValueChange={(val) => setDelivery(val === "all" ? "" : val)}>
                <SelectTrigger className="w-full md:w-44 h-10">
                  <SelectValue placeholder="Delivery Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Online/Offline/Hybrid</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Duration (e.g. weeks)"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full md:w-44 h-10"
              />
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Min £"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="w-24 h-10"
                />
                <span className="text-muted-foreground text-sm">–</span>
                <Input
                  type="number"
                  placeholder="Max £"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="w-24 h-10"
                />
              </div>
              <Button type="submit" variant="outline" className="h-10">
                Apply Filters
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">
            {page === 1 && !hasMore ? `${courses.length} courses found` : `Courses — page ${page}`}
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant={isFree ? "default" : "outline"}
              size="sm"
              onClick={() => { setIsFree(!isFree); handleSearch({ preventDefault: () => {} }); }}
              className={isFree ? "bg-emerald-600" : ""}
            >
              Free Courses
            </Button>
          </div>
        </div>

        <AdBlock position="courses-sidebar" className="mb-6" showEmpty />
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <CardContent className="p-0">
                  <Skeleton className="h-40 rounded-t-lg" />
                  <div className="p-4 space-y-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Card key={course.id} className="group hover:shadow-xl transition-all overflow-hidden" data-testid={`course-card-${course.id}`}>
                <CardContent className="p-0">
                  <div className="h-40 bg-gradient-to-br from-[#003366] to-[#004080] flex items-center justify-center relative">
                    <GraduationCap className="w-16 h-16 text-white/30" />
                    {course.is_featured && (
                      <Badge className="absolute top-3 right-3 bg-amber-500">Featured</Badge>
                    )}
                    <Badge className={`absolute top-3 left-3 ${course.provider_type === 'university' ? 'bg-blue-600' : 'bg-purple-600'} text-white`}>
                      {course.provider_type === 'university' ? 'University' : 'Course Provider'}
                    </Badge>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      {course.provider_type === 'university' ? (
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Sparkles className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className="text-sm text-muted-foreground">{course.institution_name}</span>
                    </div>
                    <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                      {course.title}
                    </h3>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge variant="secondary" className="text-xs">{course.category}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{course.level}</Badge>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {course.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <Monitor className="w-4 h-4" />
                        {course.delivery_mode}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <span className="font-medium">{course.average_rating || 4.5}</span>
                        <span className="text-muted-foreground text-sm">
                          ({course.enrollments_count?.toLocaleString() || 0})
                        </span>
                      </div>
                      <div>
                        {course.is_free ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Free</Badge>
                        ) : (
                          <div className="text-right">
                            {course.original_price && (
                              <span className="text-sm text-muted-foreground line-through mr-2">
                                {formatMoney(course.original_price, course.currency)}
                              </span>
                            )}
                            <span className="font-bold text-lg text-[#003366]">{formatMoney(course.price, course.currency)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <Link to={`/courses/${course.id}`}>
                      <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                        View Course
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <PaginationBar page={page} pageSize={PAGE_SIZE} hasMore={hasMore} onPageChange={goToPage} />
          </>
        )}
      </div>
    </div>
  );
}
